import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import api from '../services/api';
import Navbar from '../components/Navbar';

const AIAssistant = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'ai',
      content: "Hello! I'm your AI Coding Assistant. I can help you with:\n\n- Code explanations and debugging\n- Algorithm explanations\n- Best practices and code reviews\n- Language-specific questions\n- Problem-solving strategies\n- And much more!\n\nWhat would you like to know?",
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 640);
  
  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 640);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Scroll to top on component mount - force immediate scroll
  useEffect(() => {
    // Force scroll to top immediately
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    window.scrollTo(0, 0);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      console.log('Sending message to AI API:', inputMessage);
      
      const response = await api.post('/api/ai-assistant/chat', { 
        message: inputMessage 
      });

      console.log('AI API Response:', response.data);
      
      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: response.data.response,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiMessage]);
      
    } catch (error) {
      console.error('AI API Error:', error);
      
      const mockResponse = generateIntelligentResponse(inputMessage);
      
      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: mockResponse,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const generateIntelligentResponse = (message) => {
    const lowerMessage = message.toLowerCase();
    
    if (lowerMessage.includes('hello') || lowerMessage.includes('hi') || lowerMessage.includes('hey')) {
      return "Hello! I'm your AI Coding Assistant. How can I help you with your coding today?";
    }
    
    if (lowerMessage.includes('what is')) {
      if (lowerMessage.includes('java')) {
        return `Java is a popular programming language! Here's what you need to know:

**Key Features:**
• Object-Oriented Programming (OOP)
• Platform Independent (Write Once, Run Anywhere)
• Automatic Memory Management with Garbage Collection
• Rich Standard Library (java.util, java.io, etc.)
• Multi-threading Support

**Example:**
\`\`\`java
public class HelloWorld {
    public static void main(String[] args) {
        System.out.println("Hello, World!");
    }
}
\`\`\`

Java is widely used for:
- Enterprise applications
- Android mobile development
- Web applications (Spring, JSP)
- Desktop applications

Would you like to learn more about any specific Java concept?`;
      }
      
      if (lowerMessage.includes('javascript')) {
        return `JavaScript is a dynamic programming language! Here's an overview:

**Key Features:**
• Interpreted language (runs in browsers and Node.js)
• Event-driven and asynchronous
• First-class functions
• Prototype-based inheritance
• Dynamic typing

**Example:**
\`\`\`javascript
function greet(name) {
    return \`Hello, \${name}!\`;
}

console.log(greet("World"));
\`\`\`

JavaScript is essential for:
- Frontend development (React, Vue, Angular)
- Backend development (Node.js)
- Mobile apps (React Native)
- Desktop apps (Electron)

What aspect of JavaScript would you like to explore?`;
      }
      
      if (lowerMessage.includes('python')) {
        return `Python is a versatile and beginner-friendly language! Here's an overview:

**Key Features:**
• Clean and readable syntax
• Extensive standard library
• Dynamic typing
• Object-oriented and functional programming
• Great for data science and AI

**Example:**
\`\`\`python
def greet(name):
    return f"Hello, {name}!"

print(greet("World"))
\`\`\`

Python excels in:
- Data Science & Machine Learning
- Web Development (Django, Flask)
- Automation & Scripting
- Scientific Computing

What Python topic interests you?`;
      }
    }
    
    return "I'd be happy to help you with your coding question! I can assist with programming languages like JavaScript, Python, Java, web development with React and HTML/CSS, debugging techniques, algorithms, and general programming concepts. What specific topic would you like to learn about?";
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearChat = () => {
    setMessages([
      {
        id: 1,
        type: 'ai',
        content: "Hello! I'm your AI Coding Assistant. How can I help you with your coding today?",
        timestamp: new Date()
      }
    ]);
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" style={{ paddingTop: isMobile ? '60px' : '80px' }}>
        <div className="max-w-4xl mx-auto" style={{ padding: isMobile ? '12px' : '16px' }}>
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-2xl shadow-xl mb-4" style={{ padding: isMobile ? '12px' : '12px' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center" style={{ gap: isMobile ? '10px' : '12px' }}>
              <div className="relative">
                <div className="bg-white rounded-xl flex items-center justify-center shadow-lg" style={{ width: isMobile ? '36px' : '40px', height: isMobile ? '36px' : '40px' }}>
                  <span style={{ fontSize: isMobile ? '20px' : '24px' }}>🤖</span>
                </div>
                <div className="absolute -top-0.5 -right-0.5 bg-green-400 rounded-full border-2 border-white animate-pulse" style={{ width: isMobile ? '10px' : '12px', height: isMobile ? '10px' : '12px' }}></div>
              </div>
              <div>
                <h1 className="font-bold text-white" style={{ fontSize: isMobile ? '16px' : '20px' }}>YCode AI Assistant</h1>
                <p className="text-blue-100 flex items-center" style={{ fontSize: isMobile ? '10px' : '12px', gap: '4px' }}>
                  <span>💡 Powered by AI • Online</span>
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="bg-white/20 hover:bg-white/30 text-white rounded-lg font-medium backdrop-blur-sm border border-white/30 transition-all duration-200"
              style={{ 
                padding: isMobile ? '8px 12px' : '8px 16px',
                fontSize: isMobile ? '12px' : '14px',
                minHeight: isMobile ? '36px' : 'auto'
              }}
            >
              {isMobile ? '←' : '← Back'}
            </button>
          </div>
        </div>

        {/* Chat Container */}
        <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden" style={{ height: isMobile ? 'calc(100vh - 200px)' : 'calc(100vh - 240px)' }}>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto bg-gradient-to-b from-slate-50 to-white" style={{ padding: isMobile ? '12px' : '16px', gap: isMobile ? '12px' : '16px', WebkitOverflowScrolling: 'touch' }}>
            {messages.map((message) => (
              <div
                key={message.id}
                className={`relative flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} items-start`}
                style={{ marginBottom: isMobile ? '12px' : '16px' }}
              >
                {message.type === 'ai' && (
                  <div className="flex-shrink-0 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center shadow-md" style={{ width: isMobile ? '32px' : '32px', height: isMobile ? '32px' : '32px', marginRight: isMobile ? '8px' : '8px' }}>
                    <span style={{ fontSize: isMobile ? '16px' : '16px' }}>🤖</span>
                  </div>
                )}
                
                <div
                  className={`${message.type === 'user' ? '' : 'flex-1'} ${
                    message.type === 'user'
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                      : 'bg-slate-100 text-slate-900'
                  } rounded-xl shadow-sm`}
                  style={{
                    maxWidth: message.type === 'user' ? (isMobile ? '85%' : '75%') : '100%',
                    padding: isMobile ? '12px' : '12px 16px',
                    paddingTop: isMobile ? '10px' : '12px',
                    paddingBottom: isMobile ? '10px' : '12px',
                    marginLeft: message.type === 'ai' ? (isMobile ? '0' : '8px') : '0',
                    marginRight: message.type === 'user' ? (isMobile ? '0' : '0') : '0'
                  }}
                >
                  <div className={`leading-relaxed text-left ${message.type === 'ai' ? 'markdown-content' : ''}`} style={{ fontSize: isMobile ? '14px' : '14px' }}>
                    {message.type === 'ai' ? (
                      <ReactMarkdown
                        components={{
                          // Style headings
                          h1: ({node, children, ...props}) => <h1 className="font-bold mb-2 text-slate-800" style={{ fontSize: isMobile ? '18px' : '20px' }} {...props}>{children}</h1>,
                          h2: ({node, children, ...props}) => <h2 className="font-bold mb-2 text-slate-800" style={{ fontSize: isMobile ? '16px' : '18px' }} {...props}>{children}</h2>,
                          h3: ({node, children, ...props}) => <h3 className="font-bold mb-1 text-slate-700" style={{ fontSize: isMobile ? '15px' : '16px' }} {...props}>{children}</h3>,
                          // Style bold text
                          strong: ({node, ...props}) => <strong className="font-bold text-slate-900" {...props} />,
                          // Style lists
                          ul: ({node, ...props}) => <ul className="list-disc list-inside my-3 space-y-2 ml-2" {...props} />,
                          ol: ({node, ...props}) => <ol className="list-decimal list-inside my-3 space-y-2 ml-2" {...props} />,
                          li: ({node, ...props}) => <li className="ml-2 leading-relaxed" style={{ fontSize: isMobile ? '14px' : '14px' }} {...props} />,
                          // Style code blocks
                          code: ({node, inline, ...props}) => 
                            inline 
                              ? <code className="bg-slate-200 px-1.5 py-0.5 rounded font-mono text-slate-800" style={{ fontSize: isMobile ? '12px' : '12px' }} {...props} />
                              : <code className="block bg-slate-200 rounded-lg my-2 font-mono overflow-x-auto text-slate-800" style={{ padding: isMobile ? '12px' : '12px', fontSize: isMobile ? '12px' : '12px' }} {...props} />,
                          // Style paragraphs
                          p: ({node, ...props}) => <p className="mb-3 leading-relaxed" style={{ fontSize: isMobile ? '14px' : '14px' }} {...props} />,
                        }}
                      >
                        {message.content}
                      </ReactMarkdown>
                    ) : (
                      <div className="whitespace-pre-wrap break-words" style={{ fontSize: isMobile ? '14px' : '14px' }}>{message.content}</div>
                    )}
                  </div>
                  <div className={`mt-2 flex items-center`} style={{ 
                    fontSize: isMobile ? '10px' : '10px',
                    gap: '4px',
                    color: message.type === 'user' ? 'rgba(255, 255, 255, 0.8)' : '#64748b'
                  }}>
                    <span>{message.timestamp.toLocaleTimeString()}</span>
                  </div>
                </div>

                {message.type === 'user' && (
                  <div className="flex-shrink-0 bg-gradient-to-br from-green-500 to-teal-600 rounded-lg flex items-center justify-center shadow-md" style={{ width: isMobile ? '32px' : '32px', height: isMobile ? '32px' : '32px', marginLeft: isMobile ? '8px' : '8px' }}>
                    <span style={{ fontSize: isMobile ? '16px' : '16px' }}>👤</span>
                  </div>
                )}
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start items-start" style={{ marginBottom: isMobile ? '12px' : '16px' }}>
                <div className="flex-shrink-0 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center shadow-md" style={{ width: isMobile ? '32px' : '32px', height: isMobile ? '32px' : '32px', marginRight: isMobile ? '8px' : '8px' }}>
                  <span style={{ fontSize: isMobile ? '16px' : '16px' }}>🤖</span>
                </div>
                <div className="flex-1 bg-slate-100 rounded-xl shadow-sm" style={{ padding: isMobile ? '12px' : '12px 16px' }}>
                  <div className="flex items-center" style={{ gap: isMobile ? '8px' : '8px' }}>
                    <div className="flex" style={{ gap: '4px' }}>
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                    <span className="text-slate-600" style={{ fontSize: isMobile ? '12px' : '12px' }}>AI is thinking...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="border-t border-slate-200 bg-white" style={{ padding: isMobile ? '12px' : '12px' }}>
            <div className="flex" style={{ gap: isMobile ? '8px' : '8px', flexDirection: isMobile ? 'column' : 'row' }}>
              <div className="flex-1">
                <textarea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={isMobile ? "Type your question..." : "Type your question... (Enter to send, Shift+Enter for new line)"}
                  className="w-full border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none shadow-sm hover:border-slate-400 transition-colors"
                  style={{
                    padding: isMobile ? '12px' : '12px',
                    fontSize: isMobile ? '16px' : '14px',
                    minHeight: isMobile ? '80px' : 'auto',
                    rows: isMobile ? 3 : 2
                  }}
                  rows={isMobile ? 3 : 2}
                  disabled={isLoading}
                />
              </div>
              <div className="flex" style={{ gap: isMobile ? '8px' : '8px', flexDirection: isMobile ? 'row' : 'column' }}>
                <button
                  onClick={handleSendMessage}
                  disabled={!inputMessage.trim() || isLoading}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-all duration-200 shadow-md hover:shadow-lg"
                  style={{
                    padding: isMobile ? '12px 20px' : '8px 16px',
                    fontSize: isMobile ? '14px' : '14px',
                    minHeight: isMobile ? '48px' : 'auto',
                    flex: isMobile ? 1 : 'none'
                  }}
                >
                  {isMobile ? '📤 Send' : 'Send 📤'}
                </button>
                <button
                  onClick={clearChat}
                  className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 font-medium transition-all duration-200 rounded-lg"
                  style={{
                    padding: isMobile ? '12px 16px' : '4px 12px',
                    fontSize: isMobile ? '14px' : '12px',
                    minHeight: isMobile ? '48px' : 'auto',
                    flex: isMobile ? 'none' : 'none'
                  }}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3" style={{ marginTop: isMobile ? '12px' : '12px', gap: isMobile ? '8px' : '8px' }}>
          <button
            onClick={() => setInputMessage("What is JavaScript?")}
            className="group bg-gradient-to-br from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 rounded-lg border border-blue-200 hover:border-blue-300 transition-all duration-200 text-left shadow-sm hover:shadow-md"
            style={{ padding: isMobile ? '12px' : '12px', minHeight: isMobile ? '64px' : 'auto' }}
          >
            <div className="flex items-center" style={{ gap: isMobile ? '10px' : '8px' }}>
              <div style={{ fontSize: isMobile ? '20px' : '20px' }}>💡</div>
              <div>
                <div className="font-bold text-slate-800 group-hover:text-blue-600 transition-colors" style={{ fontSize: isMobile ? '13px' : '14px' }}>Ask Questions</div>
                <div className="text-slate-600" style={{ fontSize: isMobile ? '11px' : '12px' }}>Programming concepts</div>
              </div>
            </div>
          </button>
          
          <button
            onClick={() => setInputMessage("How do I create a function in Python?")}
            className="group bg-gradient-to-br from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 rounded-lg border border-purple-200 hover:border-purple-300 transition-all duration-200 text-left shadow-sm hover:shadow-md"
            style={{ padding: isMobile ? '12px' : '12px', minHeight: isMobile ? '64px' : 'auto' }}
          >
            <div className="flex items-center" style={{ gap: isMobile ? '10px' : '8px' }}>
              <div style={{ fontSize: isMobile ? '20px' : '20px' }}>🔧</div>
              <div>
                <div className="font-bold text-slate-800 group-hover:text-purple-600 transition-colors" style={{ fontSize: isMobile ? '13px' : '14px' }}>Get Help</div>
                <div className="text-slate-600" style={{ fontSize: isMobile ? '11px' : '12px' }}>Step-by-step guidance</div>
              </div>
            </div>
          </button>
          
          <button
            onClick={() => setInputMessage("Explain object-oriented programming")}
            className="group bg-gradient-to-br from-green-50 to-teal-50 hover:from-green-100 hover:to-teal-100 rounded-lg border border-green-200 hover:border-green-300 transition-all duration-200 text-left shadow-sm hover:shadow-md"
            style={{ padding: isMobile ? '12px' : '12px', minHeight: isMobile ? '64px' : 'auto' }}
          >
            <div className="flex items-center" style={{ gap: isMobile ? '10px' : '8px' }}>
              <div style={{ fontSize: isMobile ? '20px' : '20px' }}>📚</div>
              <div>
                <div className="font-bold text-slate-800 group-hover:text-green-600 transition-colors" style={{ fontSize: isMobile ? '13px' : '14px' }}>Learn Concepts</div>
                <div className="text-slate-600" style={{ fontSize: isMobile ? '11px' : '12px' }}>Programming principles</div>
              </div>
            </div>
          </button>
        </div>
        </div>
      </div>
    </>
  );
};

export default AIAssistant;

